import DehazeIcon from '@mui/icons-material/Dehaze';
import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { isSameDay } from 'date-fns';
import { ChangeEvent, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AgCheckboxGroupPanel } from '../../common/AgCheckboxGroupPanel';
import { PopperButton } from '../../common/PopperButton';
import { RootState } from '../../store';
import { canSeeStockColumn, toDateString } from '../../utils';
import WarehouseSelector from '../stock/WarehouseSelector';
import { getCurrentUserPermissions } from '../user/redux/userSelectors';
import { inactivatePriceTableInitialColumns } from './InactivatePriceTableColumns';
import {
    getInactivatePricesCheckboxFilters,
    getInactivatePricesTable,
    getInactivatePricesTableIsLoading,
    getInactivatePricesTableSelectedPricelistDate,
    getInactivatePricesTableSelectedPricelistIds,
    getInactivatePricesTableSelectedWarehouse,
    getInactivatePricesTableValidFroms
} from './redux/inactivatePricesSelectors';
import {
    inactivatePricesTableActions,
    inactivatePricesTablePricelistDateSelected,
    inactivatePricesTableWarehouseDateSelected,
    inactivatePricesTableWarehouseSelected,
    inactivatePriceTableOpenChoosePricelistDialog,
    setInactivePriceTableShowAllArticles,
    setInactivePriceTableShowInactiveArticlesOnly
} from './redux/inactivatePricesSlices';

const mapStateToProps = (state: RootState) => ({
    isLoading: getInactivatePricesTableIsLoading(state),
    availableValidFroms: getInactivatePricesTableValidFroms(state),
    selectedDate: getInactivatePricesTableSelectedPricelistDate(state),
    warehouse: getInactivatePricesTableSelectedWarehouse(state),
    selectedPricelistIds: getInactivatePricesTableSelectedPricelistIds(state),
    checkboxFilters: getInactivatePricesCheckboxFilters(state),
    columns: getInactivatePricesTable(state).columns,
    currentUserPermissions: getCurrentUserPermissions(state)
});

type Props = ReturnType<typeof mapStateToProps>;

const initialColumns = Object.values(inactivatePriceTableInitialColumns);

const InactivatePricesButtons = ({
    isLoading,
    availableValidFroms,
    selectedDate,
    warehouse,
    selectedPricelistIds,
    checkboxFilters,
    columns,
    currentUserPermissions
}: Props) => {
    const dispatch = useDispatch();

    const hasSelectedPricelists = selectedPricelistIds.length > 0;

    const handleChoosePricelists = () => {
        dispatch(inactivatePriceTableOpenChoosePricelistDialog());
    };

    const isDateEnabled = (date: Date | null): date is Date => {
        if (date == null) {
            return false;
        }

        return availableValidFroms.some(validFrom => isSameDay(new Date(validFrom), date));
    };

    const handleSetSelectedPricelistDate = (newDate: Date | null) => {
        if (isDateEnabled(newDate)) {
            dispatch(inactivatePricesTablePricelistDateSelected(toDateString(newDate)));
        }
    };

    const handleSetWarehouse = (value: unknown) => {
        dispatch(inactivatePricesTableWarehouseSelected(value as number | null));
    };

    const handleSetWarehouseDate = (newDate: Date | null) => {
        if (newDate != null) {
            dispatch(inactivatePricesTableWarehouseDateSelected(toDateString(newDate)));
        }
    };

    const handleShowAllArticles = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(setInactivePriceTableShowAllArticles(event.target.checked));
    };

    const handleShowInactivatedArticlesOnly = (event: ChangeEvent<HTMLInputElement>) => {
        dispatch(setInactivePriceTableShowInactiveArticlesOnly(event.target.checked));
    };

    const columnKeys = useMemo(() => columns.filter(x => !x.hide).map(x => x.key), [columns]);

    const handleColumnsSelected = (newColumns: string[]) => {
        dispatch(inactivatePricesTableActions.columnsSelected(newColumns));
    };

    return (
        <Grid container alignItems="flex-start" direction="row" wrap="nowrap" justifyContent="space-between">
            <Grid item>
                <Grid container spacing={2}>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={handleChoosePricelists}>
                            Choose pricelists
                        </Button>
                    </Grid>
                    <Grid item>
                        <WarehouseSelector
                            onSetWarehouse={handleSetWarehouse}
                            selectedWarehouse={warehouse?.id ?? null}
                        />
                    </Grid>
                    <Grid item>
                        {availableValidFroms.length > 0 && (
                            <DatePicker
                                inputFormat="yyyy-MM-dd"
                                label="Warehouse date"
                                value={warehouse.date}
                                onChange={handleSetWarehouseDate}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        sx={{ marginTop: theme => theme.spacing(1), width: 150 }}
                                    />
                                )}
                            />
                        )}
                    </Grid>
                    <Grid item>
                        {availableValidFroms.length > 0 && (
                            <DatePicker
                                inputFormat="yyyy-MM-dd"
                                label="Pricelist date"
                                value={selectedDate}
                                onChange={handleSetSelectedPricelistDate}
                                shouldDisableDate={date => !isDateEnabled(date)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        sx={{ marginTop: theme => theme.spacing(1), width: 150 }}
                                    />
                                )}
                            />
                        )}
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            disabled={isLoading || !hasSelectedPricelists}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showAllArticles}
                                    onChange={handleShowAllArticles}
                                />
                            }
                            label="Show all articles"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            disabled={isLoading || !hasSelectedPricelists}
                            control={
                                <Checkbox
                                    checked={checkboxFilters.showInactiveArticlesOnly}
                                    onChange={handleShowInactivatedArticlesOnly}
                                />
                            }
                            label="Show inactivated articles only"
                        />
                    </Grid>
                </Grid>
            </Grid>

            <Grid item>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <CircularProgress sx={!isLoading ? { visibility: 'hidden' } : undefined} />
                    </Grid>

                    <Grid item xs={6}>
                        <PopperButton btnIcon={<DehazeIcon />} btnTitle="Choose columns">
                            <AgCheckboxGroupPanel
                                available={initialColumns.filter(colDef =>
                                    canSeeStockColumn(colDef.field, currentUserPermissions)
                                )}
                                selected={columnKeys}
                                setSelected={handleColumnsSelected}
                            />
                        </PopperButton>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default connect(mapStateToProps)(InactivatePricesButtons);
